import { InsurableGroupLabel } from '@/features/quotes/components/quote-steps/group-life/insurable-group-label.component';
import {
  resolveMemberDisplayCapital,
  type CapitalPlanContext,
} from '@/features/quotes/utils/group-life/effective-capital';
import {
  DataTableColumn,
  Menu,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import type { PmeLifeRow } from '../../proposal.mock';
import {
  PME_LIFE_STATUS_DOT_CLASS,
  PME_LIFE_STATUS_LABEL,
  resolvePmeLifeStatus,
} from '../../utils/pme-life-status.util';

export type LivesColumnsOptions = {
  editable?: boolean;
  showIncompleteStatus?: boolean;
  /**
   * Plano da cotação/proposta. No Uniforme sem capital na vida, a coluna usa
   * o capital uniforme. Em Múltiplo Salarial o salário só é multiplicado
   * quando `multiplySalary` é verdadeiro.
   */
  plan?: CapitalPlanContext;
  /**
   * Múltiplo Salarial: a coluna mostra salário × quantidade de salários.
   */
  multiplySalary?: boolean;
  // eslint-disable-next-line no-unused-vars
  onViewDetails?: (life: PmeLifeRow) => void;
  // eslint-disable-next-line no-unused-vars
  onEdit?: (life: PmeLifeRow) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete?: (life: PmeLifeRow) => void;
};

export function createLivesColumns(
  options: LivesColumnsOptions = {}
): DataTableColumn<PmeLifeRow>[] {
  const {
    editable = false,
    showIncompleteStatus = true,
    plan,
    multiplySalary = false,
    onViewDetails,
    onEdit,
    onDelete,
  } = options;

  return [
    {
      id: 'status',
      header: 'STATUS',
      size: 90,
      sortable: false,
      cell: ({ row }) => {
        const resolvedStatus = resolvePmeLifeStatus(row.original);
        const status =
          !showIncompleteStatus && resolvedStatus === 'incomplete'
            ? 'complete'
            : resolvedStatus;
        return (
          <span
            className={`inline-block h-2.5 w-2.5 rounded-full ${PME_LIFE_STATUS_DOT_CLASS[status]}`}
            title={
              !showIncompleteStatus && status === 'complete'
                ? 'Sem afastamento'
                : PME_LIFE_STATUS_LABEL[status]
            }
          />
        );
      },
    },
    {
      accessorKey: 'insurableGroup',
      header: 'GRUPO SEGURÁVEL',
      size: 160,
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>
          <InsurableGroupLabel value={row.original.insurableGroup} />
        </Text>
      ),
    },
    {
      accessorKey: 'capital',
      header: 'CAPITAL',
      size: 140,
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>
          {resolveMemberDisplayCapital(row.original.capital, plan, {
            multiplySalary: multiplySalary || !!row.original.multiplySalary,
          }) || row.original.capital}
        </Text>
      ),
    },
    {
      accessorKey: 'sex',
      header: 'SEXO',
      size: 80,
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>{row.original.sex}</Text>
      ),
    },
    {
      accessorKey: 'birthDate',
      header: 'DATA NASCIMENTO',
      size: 150,
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>
          {row.original.birthDate}
        </Text>
      ),
    },
    {
      accessorKey: 'name',
      header: 'NOME',
      size: 180,
      cell: ({ row }) => (
        <Text className='text-sm text-alba-black-500'>{row.original.name}</Text>
      ),
    },
    {
      id: 'actions',
      header: 'AÇÕES',
      size: 80,
      sortable: false,
      cell: ({ row }) => {
        const life = row.original;
        return (
          <div className='flex justify-end'>
            <Menu.Root>
              <Menu.Trigger />
              <Menu.Content>
                <Menu.Item
                  value='details'
                  icon={<Eye size={16} />}
                  onSelect={() => onViewDetails?.(life)}
                >
                  Ver detalhes
                </Menu.Item>
                {editable ? (
                  <>
                    <Menu.Item
                      value='edit'
                      icon={<Pencil size={16} />}
                      onSelect={() => onEdit?.(life)}
                    >
                      Editar
                    </Menu.Item>
                    <Menu.Item
                      value='delete'
                      icon={<Trash2 size={16} />}
                      onSelect={() => onDelete?.(life)}
                    >
                      Excluir
                    </Menu.Item>
                  </>
                ) : null}
              </Menu.Content>
            </Menu.Root>
          </div>
        );
      },
    },
  ];
}
